'use client';

/* ============================================================================
 * TemplateDetailV2 - one Instant Site template in depth (/templates/<slug>/).
 * ============================================================================
 * Shared screen for the six template detail pages. Section order:
 *   1  Hero      - name, positioning, large preview
 *   2  Design    - the design system, trait checklist
 *   3  Best for  - three audience cards
 *   4  FAQ       - template-specific questions (FAQPage schema in the route)
 *   5  Explore   - the other five templates, cross-linked
 *   6  Closing   - demo CTA
 * Copy lives in messages/<locale>.json under Pages.TemplateDetail:
 * `common` holds shared labels, `items.<slug>` the per-template copy.
 * ========================================================================= */

import React, {useState} from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import Image from 'next/image';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';
import TemplateShowcase from '@/components/v2/templateShowcase/TemplateShowcase';

const NS = 'Pages.TemplateDetail';
const TOTAL = 6;

export const TEMPLATE_SLUGS = [
  'classic',
  'modern',
  'editorial',
  'minimal',
  'resort',
  'futuristic'
];

/* Preview components are keyed by the capitalised template name. */
const SLUG_TO_NAME = {
  classic: 'Classic',
  modern: 'Modern',
  editorial: 'Editorial',
  minimal: 'Minimal',
  resort: 'Resort',
  futuristic: 'Futuristic'
};

function Container({children, className = ''}) {
  return <div className={`container-page ${className}`}>{children}</div>;
}

function SectionAnchor({id, label}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">{label}</span>
    </Reveal>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHero({slug, name}) {
  const t = useTranslations(NS);
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
      <Container>
        <SectionAnchor id={1} label={t('common.anchors.hero')} />
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <Reveal eager>
              <p className="fig-label">
                {t('common.templateLabel')} &middot; {name} &middot;{' '}
                {t(`items.${slug}.tagline`)}
              </p>
            </Reveal>
            <Reveal delay={0.06} eager>
              <h1 className="headline-hero mt-5 max-w-[18ch] text-foreground">
                {t(`items.${slug}.hero.headline`)}
              </h1>
            </Reveal>
            <Reveal delay={0.12} eager>
              <p className="lead mt-6 max-w-[60ch]">{t(`items.${slug}.hero.lead`)}</p>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:self-end">
            <Reveal delay={0.18} eager>
              <div className="flex flex-wrap items-center gap-3 md:justify-end">
                <Link href="/request-demo/" className="cta-pill-brand">
                  {t('common.hero.primaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
                <Link href="/templates/" className="cta-text">
                  {t('common.hero.secondaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
        {/* Full-width, high-fidelity homepage mockup of the template */}
        <Reveal delay={0.14} eager>
          <div className="relative mt-14">
            <div className="glow-brand-soft" />
            <div className="relative mx-auto max-w-4xl">
              <TemplateShowcase slug={slug} />
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionDesign({slug}) {
  const t = useTranslations(NS);
  const bullets = t.raw(`items.${slug}.design.bullets`);
  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={2} label={t('common.anchors.design')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <p className="fig-label">{t('common.designLabel')}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="headline-section mt-4 max-w-[18ch] text-foreground">
                {t(`items.${slug}.design.headline`)}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="lead mt-6">{t(`items.${slug}.design.body`)}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="surface-elevated p-7 md:p-8">
              <RevealStagger as="ul" mode="clone" className="space-y-4" gap={0.05}>
                {bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[14.5px] leading-relaxed text-foreground"
                  >
                    <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </RevealStagger>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionBestFor({slug}) {
  const t = useTranslations(NS);
  const items = t.raw(`items.${slug}.bestFor.items`);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={3} label={t('common.anchors.bestFor')} />
        <Reveal>
          <p className="fig-label">{t('common.bestForLabel')}</p>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="headline-section mt-4 max-w-[22ch] text-foreground">
            {t(`items.${slug}.bestFor.headline`)}
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="lead mt-6 max-w-[60ch]">{t(`items.${slug}.bestFor.lead`)}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06}>
              <article className="surface-elevated h-full p-7">
                <h3 className="text-lg font-medium tracking-tight text-foreground">
                  {item.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-foreground-muted">
                  {item.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function FaqItem({item, open, onToggle}) {
  return (
    <li className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-6 text-left"
      >
        <span className="text-[16px] font-medium tracking-tight text-foreground md:text-lg">
          {item.q}
        </span>
        <span
          aria-hidden
          className={`mt-1 flex size-6 flex-shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-transform duration-200 ${
            open ? 'rotate-45' : ''
          }`}
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-[70ch] pb-6 text-[14.5px] leading-relaxed text-foreground-muted">
            {item.a}
          </p>
        </div>
      </div>
    </li>
  );
}

function SectionFaq({slug}) {
  const t = useTranslations(NS);
  const items = t.raw(`items.${slug}.faq`);
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={4} label={t('common.anchors.faq')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="headline-section max-w-[14ch] text-foreground">
                {t('common.faq.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.08}>
              <ul className="border-t border-border">
                {items.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  />
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionExplore({slug}) {
  const t = useTranslations(NS);
  const others = TEMPLATE_SLUGS.filter((s) => s !== slug);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={5} label={t('common.anchors.explore')} />
        <Reveal>
          <h2 className="headline-section max-w-[18ch] text-foreground">
            {t('common.explore.headline')}
          </h2>
        </Reveal>
        <Reveal delay={0.06}>
          <p className="lead mt-6 max-w-[58ch]">{t('common.explore.lead')}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {others.map((s, i) => (
            <Reveal key={s} delay={i * 0.05}>
              <Link
                href={`/templates/${s}/`}
                className="surface-elevated group block h-full overflow-hidden p-4"
              >
                <span className="relative block aspect-[4/3] overflow-hidden rounded-lg">
                  <Image
                    src={`/images/templates/tpl-${s}-hero.webp`}
                    alt=""
                    fill
                    sizes="240px"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </span>
                <div className="mt-4 flex items-baseline justify-between gap-2 px-1">
                  <span className="text-[15px] font-medium tracking-tight text-foreground">
                    {SLUG_TO_NAME[s]}
                  </span>
                  <span className="cta-text-brand text-[12px]">
                    {t('common.explore.viewLabel')} <span aria-hidden>&rarr;</span>
                  </span>
                </div>
                <p className="mt-1 px-1 text-[12.5px] text-foreground-subtle">
                  {t(`items.${s}.tagline`)}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function SectionClosing({slug, name}) {
  const t = useTranslations(NS);
  return (
    <section className="section-rhythm-lg bg-background-subtle">
      <Container>
        <SectionAnchor id={6} label={t('common.anchors.hero')} />
        <Reveal>
          <h2 className="headline-manifesto max-w-[20ch] text-foreground">
            {t('common.closing.headline', {name})}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="lead mt-6 max-w-[52ch]">{t('common.closing.body')}</p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/request-demo/" className="cta-pill-brand">
              {t('common.closing.primaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/features/" className="cta-text">
              {t('common.closing.secondaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export default function TemplateDetailV2({slug}) {
  const name = SLUG_TO_NAME[slug];
  return (
    <>
      <SectionHero slug={slug} name={name} />
      <SectionDesign slug={slug} />
      <SectionBestFor slug={slug} />
      <SectionFaq slug={slug} />
      <SectionExplore slug={slug} />
      <SectionClosing slug={slug} name={name} />
    </>
  );
}
